import { useEffect, useState, useCallback } from 'react';
import { webSocketManager, WebSocketConnectionStatus, WebSocketMessage } from '../services/websocket';
import { useAuth } from '../contexts/AuthContext';

interface UseWebSocketReturn {
  isConnected: boolean;
  connectionStatus: WebSocketConnectionStatus;
  lastMessage: WebSocketMessage | null;
  connect: () => Promise<void>;
  disconnect: () => void;
}

export function useWebSocket(): UseWebSocketReturn {
  const { tokens, isAuthenticated } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState<WebSocketConnectionStatus>('disconnected');
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);

  // Connect function
  const connect = useCallback(async () => {
    if (!tokens?.idToken) {
      console.warn('[useWebSocket] No ID token available');
      return;
    }

    try {
      await webSocketManager.connect(tokens.idToken);
    } catch (error) {
      console.error('[useWebSocket] Connection failed:', error);
    }
  }, [tokens?.idToken]);

  // Disconnect function
  const disconnect = useCallback(() => {
    webSocketManager.disconnect();
  }, []);

  // Set up status change listener
  useEffect(() => {
    const unsubscribe = webSocketManager.onStatusChange((status) => {
      setConnectionStatus(status);
    });

    return unsubscribe;
  }, []);

  // Set up generic message listener for lastMessage
  useEffect(() => {
    const unsubscribe = webSocketManager.onMessage('*', (data) => {
      setLastMessage({ type: '*', data });
    });

    // Actually, we need to listen to all message types, so let's use a different approach
    // We'll create a custom listener that captures all messages
    return unsubscribe;
  }, []);

  // Auto-connect when authenticated
  useEffect(() => {
    if (isAuthenticated && tokens?.idToken && connectionStatus === 'disconnected') {
      connect().catch(console.error);
    } else if (!isAuthenticated && connectionStatus !== 'disconnected') {
      disconnect();
    }
  }, [isAuthenticated, tokens?.idToken, connectionStatus, connect, disconnect]);

  return {
    isConnected: connectionStatus === 'connected',
    connectionStatus,
    lastMessage,
    connect,
    disconnect,
  };
}

// Hook for listening to specific message types
export function useWebSocketMessage<T = any>(
  messageType: string,
  handler: (data: T) => void,
  dependencies: any[] = []
): void {
  useEffect(() => {
    const unsubscribe = webSocketManager.onMessage(messageType, handler);
    return unsubscribe;
  }, [messageType, handler, ...dependencies]);
}

// Hook for WebSocket connection status
export function useWebSocketStatus(): {
  status: WebSocketConnectionStatus;
  isConnected: boolean;
  isConnecting: boolean;
  isDisconnected: boolean;
  isError: boolean;
} {
  const [status, setStatus] = useState<WebSocketConnectionStatus>('disconnected');

  useEffect(() => {
    const unsubscribe = webSocketManager.onStatusChange(setStatus);
    return unsubscribe;
  }, []);

  return {
    status,
    isConnected: status === 'connected',
    isConnecting: status === 'connecting',
    isDisconnected: status === 'disconnected',
    isError: status === 'error',
  };
}
